import { useEffect, useState } from 'react'
import { Regimen } from '../../../../../types/Regimen'

interface ImplantAdjustmentsProps {
  dentalProcedures: Regimen[]
  isSaved: boolean
  currentImplants: string
  implantTotalTeeth: string
  dentalItems: { [itemId: string]: string }
}

export const useImplantAdjustments = ({
  dentalProcedures,
  isSaved,
  currentImplants,
  implantTotalTeeth,
  dentalItems,
}: ImplantAdjustmentsProps) => {
  const [adjustedImplants, setAdjustedImplants] = useState(currentImplants)
  const [adjustedTotalImplants, setAdjustedTotalImplants] =
    useState(implantTotalTeeth)
  const [hasImplantAdjustments, setHasImplantAdjustments] = useState(false)
  const [hasTotalImplantAdjustments, setHasTotalImplantAdjustments] =
    useState(false)

  useEffect(() => {
    if (isSaved && dentalProcedures.length > 0) {
      let current = Number(currentImplants) || 0
      let total = Number(implantTotalTeeth) || 0
      let hasAdjustment = false
      let hasTotalAdjustment = false

      // Process all dental procedures
      dentalProcedures.forEach((procedure) => {
        const lastUsage = procedure.usages[procedure.usages.length - 1]
        const procedureId = lastUsage?.fields?.dentalProcedureId
        const procedureName = dentalItems[procedureId]?.toLowerCase() || ''

        // Check for procedures that add implants
        if (procedureName.includes('dental implant placed')) {
          current += 1
          total += 1
          hasAdjustment = true
          hasTotalAdjustment = true
        }

        // Check for procedures that remove implants
        if (procedureName.includes('dental implant removed')) {
          current = Math.max(0, current - 1)
          hasAdjustment = true
        }
      })

      setAdjustedImplants(current.toString())
      setAdjustedTotalImplants(total.toString())
      setHasImplantAdjustments(hasAdjustment)
      setHasTotalImplantAdjustments(hasTotalAdjustment)
    } else {
      // If not saved or no procedures, use the original values
      setAdjustedImplants(currentImplants)
      setAdjustedTotalImplants(implantTotalTeeth)
      setHasImplantAdjustments(false)
      setHasTotalImplantAdjustments(false)
    }
  }, [
    dentalProcedures,
    isSaved,
    currentImplants,
    implantTotalTeeth,
    dentalItems,
  ])

  return {
    adjustedImplants,
    adjustedTotalImplants,
    hasImplantAdjustments,
    hasTotalImplantAdjustments,
  }
}

export default useImplantAdjustments
